/** The signed-in member and what their roles allow, for every page. */
import { createContext, useContext } from "react";
import { canDo, canRead, type Grants } from "../../shared/areas";

export type Me = {
  member: { id: string; name: string; email: string; last_factor: string | null };
  factor: string;
  roles: { id: string; name: string; applies: boolean; reason?: string }[];
  grants: Grants;
  expires_at: string;
  environment: string;
  host: string;
  /** The newest platform release the Brain recorded (empty before the first), and the Brain's own commit and build time. */
  version: { version: string; build: string; commit: string; built_at: string };
  readable: string[];
};

export const MeCtx = createContext<Me | null>(null);

export function useMe(): Me {
  const me = useContext(MeCtx);
  if (!me) throw new Error("useMe outside the signed-in app");
  return me;
}

/** can("tenants") reads; can("tenants", "licence") does. */
export function useCan() {
  const me = useMe();
  return (area: string, action?: string) => (action === undefined ? canRead(me.grants, area) : canDo(me.grants, area, action === "write" ? undefined : action));
}

/** The sentence a disabled control shows, naming the member's roles. */
export function useWhyNot() {
  const me = useMe();
  const roles = me.roles.filter((r) => r.applies).map((r) => r.name).join(", ") || "none";
  return (what: string) => `Your roles (${roles}) don't allow ${what}.`;
}
